import React from 'react'

import { debounce } from 'lodash-es'
import { DataItem, load, navigateTo, Obj } from 'scrivito'

import { SearchBar } from './SearchBar'
import { SearchOverlay } from './SearchOverlay'
import { SearchResultsCard } from './searchResults/SearchResultsCard'
import { PagePermaLink } from '@/const'
import { FeatureFlags } from '@/Data/const'
import { GlobalResult } from '@/Data/GlobalResult/GlobalResultDataClass'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { useFeatureFlagAccess } from '@/utils/featureFlagConfig'

const SEARCH_ITEM_LIMIT = 3

export const Search: React.FC = () => {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [isHidden, setIsHidden] = React.useState(false)
  const [searchKey, setSearchKey] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<DataItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  const { isFeatureFlagEnabled } = useFeatureFlagAccess()

  const showSearchResults = () => setIsHidden(false)
  const hideSearchResults = () => setIsHidden(true)

  const search = React.useCallback((input: string) => {
    load(() =>
      input
        ? GlobalResult.all()
            .transform({
              search: input,
              limit: SEARCH_ITEM_LIMIT,
            })
            .take()
        : [],
    )
      .then((searchResults) => {
        setSearchResults(searchResults)
      })
      .catch((error) => {
        setError(error)
        console.log(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const debouncedSearch = React.useMemo(
    () => debounce((value) => search(value), 1000),
    [search],
  )

  const debouncer = (value: string) => {
    setError('')
    setLoading(true)
    debouncedSearch(value)
  }

  const showAllResults = () => {
    navigateTo(() => Obj.getByPermalink(PagePermaLink.SEARCH_RESULTS), {
      params: { search: searchKey },
    })
    hideSearchResults()
  }

  useOnClickOutside({ elementRef: ref, onClick: hideSearchResults })

  if (!isFeatureFlagEnabled(FeatureFlags.SHOW_GLOBAL_SEARCH)) {
    return <div className="mx-auto" />
  }

  return (
    <>
      <div ref={ref} className="dxp-searchbar mx-auto">
        <SearchBar
          debouncer={debouncer}
          setSearchKey={setSearchKey}
          showSearchResults={showSearchResults}
        />
        {!isHidden && searchKey && (
          <SearchOverlay onClick={hideSearchResults} />
        )}
        {!isHidden && searchKey && (
          <SearchResultsCard
            searchResults={searchResults}
            hideSearchResults={hideSearchResults}
            showAllResults={showAllResults}
            loading={loading}
            error={error}
          />
        )}
      </div>
    </>
  )
}
